import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building, Eye, EyeOff, KeyRound, Phone } from 'lucide-react';
import { useId, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type MailRecipientOption, type UserRef } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { isEnglish, plainText } from '../lib/english';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { generatePassword } from '../lib/passwords';
import { FormGrid, FormPage } from '../components/FormPage';
import { LogoPicker, type LogoValue } from '../components/LogoPicker';
import { PeoplePicker } from '../components/PeoplePicker';
import { Badge, Button, Card, CardHeader, Checkbox, Field, Input, Select, Textarea, useToast } from '../components/ui';
import { useCountries, useCurrencies } from './Clients';

/** An employee a client may call from the portal, as the client's record reads them back (2026-11-01). */
export interface ClientCaller {
  id: string;
  fullName: string;
  fullNameAr: string | null;
  jobTitle: string | null;
  isActive: boolean;
}

/** The most employees one client may call — the API's own limit. */
const MAX_CALLERS = 200;

/** A portal sign-in as the admin reads it back — never its password. */
export interface PortalSignIn {
  username: string;
  isActive: boolean;
  lastLoginAt: string | null;
}

/** What the sign-in card holds while the form is open. */
interface PortalDraft {
  enabled: boolean;
  username: string;
  password: string;
}

const emptyDraft = (account?: PortalSignIn | null): PortalDraft => ({
  enabled: account?.isActive ?? false,
  username: account?.username ?? '',
  password: '',
});

/** Why the sign-in cannot be saved as it stands, if it cannot: a new one needs both. */
function portalProblem(draft: PortalDraft, account: PortalSignIn | null | undefined): TranslationKey | null {
  if (draft.enabled && (!draft.username.trim() || (!account && !draft.password))) return 'clients.portalNeedsBoth';
  if (draft.password && draft.password.length < 8) return 'clients.portalPasswordHint';
  return null;
}

/** What a save sends about a sign-in: nothing unless something changed. */
function portalBody(draft: PortalDraft, account: PortalSignIn | null | undefined) {
  if (!account) {
    return draft.enabled ? { enabled: true, username: draft.username.trim(), password: draft.password } : undefined;
  }
  const changes: { enabled?: boolean; username?: string; password?: string } = {};
  if (draft.enabled !== account.isActive) changes.enabled = draft.enabled;
  if (draft.username.trim() && draft.username.trim().toLowerCase() !== account.username) {
    changes.username = draft.username.trim();
  }
  if (draft.password) changes.password = draft.password;
  return Object.keys(changes).length ? changes : undefined;
}

/**
 * A sign-in to the client portal: the company's own on its form, or a
 * contact's on theirs (2026-10-11) — the same fields, the same rules.
 */
function PortalSignInCard({
  title,
  hint,
  enabledLabel,
  draft,
  account,
  onChange,
}: {
  title: ReactNode;
  hint: ReactNode;
  enabledLabel: ReactNode;
  draft: PortalDraft;
  account: PortalSignIn | null | undefined;
  onChange: (draft: PortalDraft) => void;
}) {
  const { t } = useI18n();
  const [showPassword, setShowPassword] = useState(false);
  const problem = portalProblem(draft, account);

  return (
    <Card className="mt-5">
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden />
            {title}
          </span>
        }
        subtitle={hint}
      />
      <Checkbox
        checked={draft.enabled}
        onChange={(e) => onChange({ ...draft, enabled: e.target.checked })}
        label={enabledLabel}
      />
      {(draft.enabled || account) && (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label={t('clients.portalUsername')} required={draft.enabled} hint={t('clients.portalUsernameHint')}>
            <Input
              value={draft.username}
              onChange={(e) => onChange({ ...draft, username: e.target.value })}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              maxLength={40}
              dir="ltr"
            />
          </Field>
          <Field
            label={account ? t('clients.portalNewPassword') : t('clients.portalPassword')}
            required={draft.enabled && !account}
            hint={account ? t('clients.portalKeepPassword') : t('clients.portalPasswordHint')}
          >
            <div className="flex gap-2">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={draft.password}
                onChange={(e) => onChange({ ...draft, password: e.target.value })}
                autoComplete="new-password"
                dir="ltr"
                className="min-w-0 flex-1"
              />
              <Button
                icon={showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                aria-label={showPassword ? t('clients.portalHide') : t('clients.portalShow')}
                title={showPassword ? t('clients.portalHide') : t('clients.portalShow')}
                onClick={() => setShowPassword(!showPassword)}
              />
              <Button
                onClick={() => {
                  onChange({ ...draft, password: generatePassword() });
                  setShowPassword(true);
                }}
              >
                {t('clients.portalGenerate')}
              </Button>
            </div>
          </Field>
        </div>
      )}
      {problem && (draft.username || draft.password) && (
        <p className="mt-3 text-caption text-warning-fg dark:text-warning-dark">{t(problem)}</p>
      )}
    </Card>
  );
}

/** A department the client may be given: `isActive` false only for one it already had. */
interface DepartmentChoice {
  id: string;
  name: string;
  nameAr: string | null;
  code: string;
  isActive: boolean;
}

/**
 * CR-001 §2–§5 — creating and editing a client company, as a page.
 *
 * The same fields, in the same order, as Finance's client form
 * (apps/sys/src/pages/ClientForm.tsx), the logo included (operator,
 * 2026-10-11: "two forms in add and edit must be same"). The admin adds only
 * what is the administrators' own: where the client's tickets go and the
 * company's portal sign-in.
 *
 * Routes: `/clients/new` and `/clients/:id/edit`.
 */
export function ClientFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const countries = useCountries();
  const currencies = useCurrencies();
  // The company's own portal sign-in, and where its tickets may go — both the
  // administrators' (client_portal.manage).
  const managesPortal = can(P.CLIENT_PORTAL_MANAGE);
  const [portal, setPortal] = useState<PortalDraft>(emptyDraft());

  const existing = useQuery({
    queryKey: ['client', id],
    queryFn: () => api.get<any>(`/clients/${id}`),
    enabled: editing,
  });

  const [form, setForm] = useState({
    companyName: '',
    companyNameAr: '',
    legalName: '',
    code: '',
    countryCode: 'EG',
    defaultCurrency: 'EGP',
    address: '',
    taxNumber: '',
    registrationNumber: '',
    website: '',
    notes: '',
  });
  // The logo is uploaded as soon as it is picked; saving the form says which one
  // the client has. Sent only once it changed, as null when it was taken off.
  const [logo, setLogo] = useState<LogoValue | null>(null);
  const [logoChanged, setLogoChanged] = useState(false);

  // A new client is offered the last code + 1 (operator, 2026-10-07): asked for
  // afresh on every visit, and filled in once, only into an empty field — it is
  // a reminder of where the codes are up to, and can be changed like any field.
  const nextCode = useQuery({
    queryKey: ['clients', 'next-code'],
    queryFn: () => api.get<{ code: string; after: string | null }>('/clients/next-code'),
    enabled: !editing,
    gcTime: 0,
  });
  const [offered, setOffered] = useState(false);
  if (!editing && nextCode.data && !offered) {
    setOffered(true);
    if (!form.code) setForm({ ...form, code: nextCode.data.code });
  }

  // The departments the client may send its tickets to (operator, 2026-10-11:
  // "when add/edit clients i must chose departments the client has rights to
  // sent ticket to"). A new client starts with the default one ticked.
  const departmentOptions = useQuery({
    queryKey: ['clients', 'ticket-department-options'],
    queryFn: () =>
      api.get<{ departments: DepartmentChoice[]; defaultId: string | null }>('/clients/ticket-department-options'),
    enabled: managesPortal,
  });
  const [ticketDepartments, setTicketDepartments] = useState<string[] | null>(null);
  if (!editing && ticketDepartments === null && departmentOptions.data) {
    const { defaultId } = departmentOptions.data;
    setTicketDepartments(defaultId ? [defaultId] : []);
  }

  // The employees the client may call from the portal (operator, 2026-11-01: "let client call selected
  // employees we will select from client which employee can call"). A new client calls nobody until picked.
  const callerOptions = useQuery({
    queryKey: ['clients', 'callable-user-options'],
    queryFn: () => api.get<MailRecipientOption[]>('/clients/callable-user-options'),
    enabled: managesPortal,
  });
  const [callers, setCallers] = useState<string[]>([]);
  const callersId = useId();
  const heldCallers: ClientCaller[] = existing.data?.callableUsers ?? [];
  // Names for the ones the client already has who are no longer active, so they show as picked.
  const knownCallers = useMemo(
    () => new Map<string, UserRef>(((existing.data?.callableUsers ?? []) as ClientCaller[]).map((u) => [u.id, u])),
    [existing.data],
  );

  // Seed the form once the record arrives; `loaded` keeps later edits intact.
  const [loaded, setLoaded] = useState(false);
  if (editing && existing.data && !loaded) {
    setLoaded(true);
    setForm({
      companyName: existing.data.companyName ?? '',
      companyNameAr: existing.data.companyNameAr ?? '',
      legalName: existing.data.legalName ?? '',
      code: existing.data.code ?? '',
      countryCode: existing.data.countryCode ?? 'EG',
      defaultCurrency: existing.data.defaultCurrency ?? 'EGP',
      address: existing.data.address ?? '',
      taxNumber: existing.data.taxNumber ?? '',
      registrationNumber: existing.data.registrationNumber ?? '',
      website: existing.data.website ?? '',
      notes: existing.data.notes ?? '',
    });
    setLogo(existing.data.logo ?? null);
    setPortal(emptyDraft(existing.data.account));
    setTicketDepartments((existing.data.ticketDepartments ?? []).map((d: DepartmentChoice) => d.id));
    setCallers((existing.data.callableUsers ?? []).map((u: ClientCaller) => u.id));
  }
  const account = existing.data?.account as PortalSignIn | null | undefined;
  const signInProblem = managesPortal ? portalProblem(portal, account) : null;

  // Every department switched on, and any the client already has that was switched off since.
  const held: DepartmentChoice[] = existing.data?.ticketDepartments ?? [];
  const departmentChoices: DepartmentChoice[] = [
    ...(departmentOptions.data?.departments ?? []),
    ...held.filter((d) => !departmentOptions.data?.departments.some((o) => o.id === d.id)),
  ];
  // At least one, whenever there is one to choose.
  const departmentsMissing =
    managesPortal && departmentChoices.length > 0 && ticketDepartments !== null && ticketDepartments.length === 0;

  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...form,
        companyNameAr: form.companyNameAr || undefined,
        legalName: form.legalName || undefined,
        address: form.address || undefined,
        taxNumber: form.taxNumber || undefined,
        registrationNumber: form.registrationNumber || undefined,
        website: form.website || undefined,
        notes: form.notes || undefined,
        // Blank, a new client gets the next code and an edited one keeps its own.
        code: form.code.trim() || undefined,
        ...(logoChanged ? { logoFileId: logo?.id ?? null } : {}),
        portal: managesPortal ? portalBody(portal, account) : undefined,
        // Sent as it stands: the API records it only when it moved.
        ticketDepartmentIds: managesPortal && ticketDepartments?.length ? ticketDepartments : undefined,
        // Whom it may call, as it stands — none at all is a choice too.
        callableUserIds: managesPortal ? callers : undefined,
      };
      return editing ? api.patch<any>(`/clients/${id}`, body) : api.post<any>('/clients', body);
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      void queryClient.invalidateQueries({ queryKey: ['client', saved.id] });
      void queryClient.invalidateQueries({ queryKey: ['client-options'] });
      navigate(`/clients/${saved.id}`);
    },
    onError: (e: any) => {
      if (e?.code === 'DUPLICATE_RESOURCE' && e?.details?.field === 'code' && form.code.trim()) {
        return toast.error(t('clients.codeTaken', { code: form.code.trim() }));
      }
      if (e?.details?.field === 'logoFileId' || e?.code === 'FILE_NOT_ATTACHABLE') {
        return toast.error(t('clients.logoRefused', { reason: e.message }));
      }
      if (e?.details?.field === 'ticketDepartmentIds') {
        // A department switched off or removed while the form was open: offer what is left.
        void queryClient.invalidateQueries({ queryKey: ['clients', 'ticket-department-options'] });
        return toast.error(t('clients.ticketDepartmentsGone'));
      }
      if (e?.details?.field === 'callableUserIds') {
        // Someone switched off or deleted while the form was open: offer who is left.
        void queryClient.invalidateQueries({ queryKey: ['clients', 'callable-user-options'] });
        return toast.error(t('clients.callersGone'));
      }
      toast.error(e.message);
    },
  });

  const codeHint = editing
    ? t('clients.codeEditHint')
    : nextCode.data?.after
      ? t('clients.codeFollows', { code: nextCode.data.after })
      : nextCode.data
        ? t('clients.codeFirst')
        : t('clients.codeHint');

  const toggleDepartment = (departmentId: string, on: boolean) =>
    setTicketDepartments((current) => {
      const list = current ?? [];
      return on ? [...list.filter((d) => d !== departmentId), departmentId] : list.filter((d) => d !== departmentId);
    });

  return (
    <FormPage
      title={editing ? `${t('common.edit')} — ${form.companyName}` : t('clients.new')}
      backTo={editing ? `/clients/${id}` : '/clients'}
      backLabel={editing ? form.companyName || t('clients.title') : t('clients.title')}
      submitLabel={editing ? t('common.save') : t('common.create')}
      submitting={save.isPending}
      disabled={
        !form.companyName || !form.countryCode || !form.defaultCurrency || !!signInProblem || departmentsMissing
      }
      loading={editing && existing.isLoading}
      error={existing.error}
      onRetry={() => void existing.refetch()}
      onSubmit={() => save.mutate()}
    >
      <FormGrid>
        <Field label={t('clients.companyName')} required>
          <Input
            value={form.companyName}
            onChange={(e) => setForm({ ...form, companyName: e.target.value })}
            autoFocus
          />
        </Field>
        <Field label={t('clients.companyNameAr')}>
          <Input
            dir="rtl"
            value={form.companyNameAr}
            onChange={(e) => setForm({ ...form, companyNameAr: e.target.value })}
          />
        </Field>
        <Field label={t('clients.legalName')}>
          <Input value={form.legalName} onChange={(e) => setForm({ ...form, legalName: e.target.value })} />
        </Field>
        <Field label={t('clients.code')} hint={codeHint}>
          <Input
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            // What a blank code saves as: the next one, or the code it has.
            placeholder={editing ? existing.data?.code : nextCode.data?.code}
            maxLength={40}
            dir="ltr"
          />
        </Field>

        <Field label={t('clients.country')} required>
          <Select
            value={form.countryCode}
            onChange={(e) => {
              const country = (countries.data ?? []).find((c) => c.code === e.target.value);
              setForm({
                ...form,
                countryCode: e.target.value,
                // A hint, not a rule: §5 says the currency is chosen. Pre-filling
                // the usual one still leaves it editable right below.
                defaultCurrency: country?.defaultCurrency ?? form.defaultCurrency,
              });
            }}
          >
            {(countries.data ?? []).map((c) => (
              <option key={c.code} value={c.code}>{localized(c)}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('clients.currency')} required hint={t('clients.currencyHint')}>
          <Select
            value={form.defaultCurrency}
            onChange={(e) => setForm({ ...form, defaultCurrency: e.target.value })}
          >
            {(currencies.data ?? []).map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} — {locale === 'ar' && c.nameAr ? c.nameAr : c.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('clients.taxNumber')}>
          <Input value={form.taxNumber} onChange={(e) => setForm({ ...form, taxNumber: e.target.value })} />
        </Field>
        <Field label={t('clients.registrationNumber')}>
          <Input
            value={form.registrationNumber}
            onChange={(e) => setForm({ ...form, registrationNumber: e.target.value })}
          />
        </Field>
        <Field label={t('clients.website')} className="sm:col-span-2">
          <Input
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
            placeholder="https://"
            dir="ltr"
          />
        </Field>
        <div className="sm:col-span-2">
          <p className="mb-1.5 text-body-sm font-medium text-[var(--text-secondary)]">{t('clients.logo')}</p>
          <LogoPicker
            value={logo}
            clientName={form.companyName || t('clients.title')}
            onChange={(next) => {
              setLogo(next);
              setLogoChanged(true);
            }}
          />
        </div>
        <Field label={t('clients.address')} className="sm:col-span-2">
          <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </Field>
        <Field label={t('common.notes')} className="sm:col-span-2">
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
      </FormGrid>

      {managesPortal && (
        <Card className="mt-5">
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                <Building className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden />
                {t('clients.ticketDepartments')}
                {departmentChoices.length > 0 && <span className="text-error-fg dark:text-error-dark" aria-hidden>*</span>}
              </span>
            }
            subtitle={t('clients.ticketDepartmentsHint')}
          />
          {departmentOptions.isLoading ? (
            <p className="text-body-sm text-muted">{t('common.loading')}</p>
          ) : departmentChoices.length === 0 ? (
            <p className="text-body-sm text-muted">{t('clients.ticketDepartmentsNone')}</p>
          ) : (
            <ul className="grid gap-2.5 sm:grid-cols-2" role="group" aria-label={t('clients.ticketDepartments')}>
              {departmentChoices.map((department) => (
                <li key={department.id} className="flex flex-wrap items-center gap-2">
                  <Checkbox
                    checked={ticketDepartments?.includes(department.id) ?? false}
                    onChange={(e) => toggleDepartment(department.id, e.target.checked)}
                    label={localized(department)}
                  />
                  {department.id === departmentOptions.data?.defaultId && (
                    <Badge tone="info">{t('clients.ticketDepartmentDefault')}</Badge>
                  )}
                  {!department.isActive && <Badge tone="neutral">{t('clients.ticketDepartmentOff')}</Badge>}
                </li>
              ))}
            </ul>
          )}
          {departmentsMissing && (
            <p className="mt-3 text-caption text-warning-fg dark:text-warning-dark">{t('clients.ticketDepartmentsRequired')}</p>
          )}
        </Card>
      )}

      {managesPortal && (
        <Card className="mt-5">
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden />
                {t('clients.callers')}
              </span>
            }
            subtitle={t('clients.callersHint')}
          />
          {/* Not a Field: that is a <label>, and a label around the chips' buttons would press them. */}
          <label htmlFor={callersId} className="sr-only">
            {t('clients.callers')}
          </label>
          <PeoplePicker
            inputId={callersId}
            people={callerOptions.data}
            value={callers}
            onChange={setCallers}
            max={MAX_CALLERS}
            known={knownCallers}
          />
          <p className="mt-2 text-caption text-faint">
            {callers.length === 0 ? t('clients.callersNone') : t('clients.callersRecorded')}
          </p>
          {heldCallers.some((u) => !u.isActive && callers.includes(u.id)) && (
            <p className="mt-1 text-caption text-warning-fg dark:text-warning-dark">{t('clients.callersInactive')}</p>
          )}
        </Card>
      )}

      {managesPortal && (
        <PortalSignInCard
          title={t('clients.portal')}
          hint={t('clients.portalHint')}
          enabledLabel={t('clients.portalEnabled')}
          draft={portal}
          account={account}
          onChange={setPortal}
        />
      )}
    </FormPage>
  );
}

/**
 * §2 — a contact person belongs to a client and is never a client themselves.
 * Since 2026-10-11 a contact may have their own sign-in to the client portal,
 * and team leaders add and edit contacts as well as the administrators. Since
 * 2026-10-12 every field is in English (`lib/english.ts`), as on Finance's form.
 *
 * Routes: `/clients/:id/contacts/new` and `/clients/:id/contacts/:contactId/edit`.
 */
export function ClientContactFormPage() {
  const { id = '', contactId } = useParams();
  const editing = !!contactId;
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const client = useQuery({
    queryKey: ['client', id],
    queryFn: () => api.get<any>(`/clients/${id}`),
  });

  const [form, setForm] = useState({ name: '', jobTitle: '', email: '', phone: '', isPrimary: false });
  const [portal, setPortal] = useState<PortalDraft>(emptyDraft());

  const contact = editing ? client.data?.contacts.find((c: any) => c.id === contactId) : undefined;
  const account = contact?.account as PortalSignIn | null | undefined;
  const [loaded, setLoaded] = useState(false);
  if (editing && contact && !loaded) {
    setLoaded(true);
    setForm({
      name: contact.name ?? '',
      jobTitle: contact.jobTitle ?? '',
      email: contact.email ?? '',
      phone: contact.phone ?? '',
      isPrimary: contact.isPrimary ?? false,
    });
    setPortal(emptyDraft(contact.account));
  }
  const signInProblem = portalProblem(portal, account);
  // Every field in English — a contact saved before with Arabic shows it here until it is rewritten.
  const notEnglish = (value: string) => (isEnglish(value) ? null : t('clients.contactEnglishOnly'));
  const anyNotEnglish = [form.name, form.jobTitle, form.email, form.phone].some((value) => !isEnglish(value));

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: plainText(form.name),
        jobTitle: plainText(form.jobTitle) || undefined,
        email: plainText(form.email) || undefined,
        phone: plainText(form.phone) || undefined,
        isPrimary: form.isPrimary,
        portal: portalBody(portal, account),
      };
      return editing
        ? api.patch(`/clients/${id}/contacts/${contactId}`, body)
        : api.post(`/clients/${id}/contacts`, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client', id] });
      navigate(`/clients/${id}`);
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={editing ? t('common.edit') : t('clients.newContact')}
      subtitle={client.data?.companyName}
      backTo={`/clients/${id}`}
      backLabel={client.data?.companyName ?? t('clients.title')}
      submitLabel={t('common.save')}
      submitting={save.isPending}
      disabled={!form.name || !!signInProblem || anyNotEnglish}
      loading={client.isLoading}
      error={client.error}
      onRetry={() => void client.refetch()}
      onSubmit={() => save.mutate()}
    >
      <FormGrid>
        <p className="text-body-sm text-muted sm:col-span-2">{t('clients.contactEnglishNote')}</p>
        <Field label={t('common.name')} required error={notEnglish(form.name)}>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            autoFocus
            dir="ltr"
          />
        </Field>
        <Field label={t('clients.jobTitle')} error={notEnglish(form.jobTitle)}>
          <Input value={form.jobTitle} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} dir="ltr" />
        </Field>
        <Field label={t('common.email')} error={notEnglish(form.email)}>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            dir="ltr"
          />
        </Field>
        <Field label={t('common.phone')} error={notEnglish(form.phone)}>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} dir="ltr" />
        </Field>
        <div className="sm:col-span-2">
          <Checkbox
            checked={form.isPrimary}
            onChange={(e) => setForm({ ...form, isPrimary: e.target.checked })}
            label={t('clients.primaryContact')}
          />
        </div>
      </FormGrid>

      {/* Optional: a contact without a sign-in is still a contact (operator, 2026-10-11). */}
      <PortalSignInCard
        title={t('clients.contactPortal')}
        hint={t('clients.contactPortalHint')}
        enabledLabel={t('clients.contactPortalEnabled')}
        draft={portal}
        account={account}
        onChange={setPortal}
      />
    </FormPage>
  );
}
